import { useEffect, useState } from "react";
import { Award, Globe2, MapPin, Palmtree, Repeat, Truck } from "lucide-react";
import { useFirstMonthOn } from "@/lib/subscription";
import { FlashBar, GiftBar } from "./FlashSale";
import { LuminaBar } from "./LuminaLaunch";
import { FormationBar } from "@/components/formation/FormationBar";
import { useFlashSale, useGiftOffer, useLuminaLaunch } from "@/lib/flash";
import { useLang } from "@/lib/i18n";
import { useShipGeo, geoMessage } from "@/lib/shipGeo";

const FIRST = { icon: Repeat, text: "Nouveau : abonnement Duo, 2 soins au choix, 1er mois à 16,99 € puis 19,99 €/mois" };

const MESSAGES = [
  { icon: Truck, text: "Livraison gratuite en France en Point Relais Mondial Relay" },
  { icon: Repeat, text: "Abonnements Duo 19,99 € et Famille 39,90 € par mois, sans engagement" },
];

const MESSAGES_EN = [
  { icon: Award, text: "Since 2011: the specialist brand for hair loss and alopecia in afro hair" },
  { icon: MapPin, text: "Europe: Mondial Relay pickup point €4.99 · France: free pickup-point delivery" },
  { icon: Repeat, text: "Made in France · Prices in euros · Secure payment" },
];
// Site anglais sans pays reconnu (ou visiteuse en France) : la grille internationale reste visible.
const EN_FREE = "Free tracked shipping: UK & Switzerland from €79 · USA, Canada & worldwide from €119";

function Group({ hidden, messages }) {
  return (
    <ul aria-hidden={hidden || undefined} className="flex shrink-0 items-center">
      {[...messages, ...messages, ...messages, ...messages].map(({ icon: Icon, text }, i) => (
        <li key={i} className="flex items-center gap-2.5 whitespace-nowrap px-8 md:px-12">
          <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--gold)]" strokeWidth={1.5} aria-hidden="true" />
          <span>{text}</span>
          <span className="ml-8 text-[var(--gold)]/60 md:ml-12" aria-hidden="true">✦</span>
        </li>
      ))}
    </ul>
  );
}

export function AnnouncementBar() {
  const flash = useFlashSale();
  const gift = useGiftOffer();
  const lumina = useLuminaLaunch();
  const first = useFirstMonthOn();
  const { lang } = useLang();
  const geo = useShipGeo();
  const local = geoMessage(geo, lang) || (lang === "en" ? EN_FREE : null);
  const [showGeo, setShowGeo] = useState(false);
  const flashOn = Boolean(flash);
  // Hors vente flash, en français : Lumina, offre cadeau et formation se relaient toutes les 5 s (avec les infos livraison s'il n'y a pas d'offre).
  const fr = lang !== "en";
  const rota = flash || !fr ? [] : [lumina && "lumina", gift && "gift", "formation", !lumina && !gift && "info"].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const rotaLen = rota.length;
  useEffect(() => {
    if (rotaLen < 2) return undefined;
    const t = setInterval(() => setIdx((v) => v + 1), 5000);
    return () => clearInterval(t);
  }, [rotaLen]);
  useEffect(() => {
    if (!flashOn || !local) return undefined;
    const t = setInterval(() => setShowGeo((v) => !v), 5000);
    return () => clearInterval(t);
  }, [flashOn, local]);
  // Pendant une vente flash, la livraison du pays alterne avec le bandeau promo toutes les 5 s (même hauteur).
  const cur = rotaLen ? rota[idx % rotaLen] : null;
  if (cur === "lumina") return <LuminaBar />;
  if (cur === "gift") return <GiftBar />;
  if (cur === "formation") return <FormationBar />;
  if (!flash && lumina) return <LuminaBar />;
  if (!flash && gift && lang !== "en") return <GiftBar />;
  if (flash && !(local && showGeo)) return <FlashBar />;
  if (flash) {
    return (
      <div data-testid="announcement-geo-shipping" role="region" aria-label={lang === "en" ? "Shipping" : "Livraison"}
        className="flex h-9 items-center justify-center gap-2.5 overflow-hidden whitespace-nowrap border-b border-black/20 bg-[var(--gold)] px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)] md:text-xs">
        <Globe2 className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" /><span className="truncate">{local}</span>
      </div>
    );
  }
  const base = lang === "en" ? MESSAGES_EN : first ? [MESSAGES[0], FIRST] : MESSAGES;
  // Hors France : la livraison du pays remplace les messages France / DOM-TOM et passe en premier.
  const messages = local ? [{ icon: Globe2, text: local }, ...base.filter((m) => m.icon !== Truck && m.icon !== Palmtree)] : base;
  return (
    <div data-testid="announcement-bar" role="region" aria-label={lang === "en" ? "Shipping and payment information" : "Informations livraison et paiement"}
      className="announce group relative flex h-9 items-center overflow-hidden border-b border-[var(--line)] bg-[var(--plum-deep)] text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--parchment)] md:text-xs">
      {local && <span data-testid="announcement-geo-shipping" className="sr-only">{local}</span>}
      <div className="announce-track flex w-max">
        <Group messages={messages} />
        <Group hidden messages={messages} />
      </div>
    </div>
  );
}
